'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { isTjtSettings, isTimesPostSettings, classicTagline } from '../data/publicationIdentity';
import { hasPaddedTimesPostMasthead, timesPostStickyCrop, timesPostMastheadMask } from '../data/masthead';
import StickyNavigationDrawer from './StickyNavigationDrawer';
import { Menu } from 'lucide-react';
import MobileNotificationControl from './MobileNotificationControl';

export default function StickyHeader({ siteSettings, publishedArticles = [], latestArticles = [] }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pubName = siteSettings?.organization?.name || 'SURGEDOME Publication';
  const tagLine = classicTagline(siteSettings);
  const isTjt = isTjtSettings(siteSettings);
  const isTimesPost = isTimesPostSettings(siteSettings);
  const logo = siteSettings?.organization?.logo || siteSettings?.organization?.logoSettings?.url;

  const generalSettings = siteSettings?.general ? 
    (typeof siteSettings.general === 'string' ? JSON.parse(siteSettings.general) : siteSettings.general) 
    : {};
  const routePrefix = generalSettings.routePrefix || null;
  const homeUrl = routePrefix ? `/${routePrefix}/` : '/';

  return (
    <>
      <div 
        className={`fixed top-0 left-0 right-0 w-full h-16 ${isTimesPost ? "" : "md:h-[88px]"} bg-white z-[90] border-b border-gray-900 transition-transform duration-200 ease-in-out ${
          isVisible ? 'translate-y-0 shadow-sm' : '-translate-y-full'
        }`}
      >
        <div className={`w-full h-full max-w-[1200px] mx-auto px-4 items-center ${isTimesPost ? "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" : "flex justify-between"}`}>
          <div className="flex-1 flex items-center justify-start">
            <button 
              onClick={() => setIsDrawerOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={isDrawerOpen}
              className="p-1 -ml-1 focus:outline-none group flex items-center"
            >
              <Menu size={22} strokeWidth={1.5} className="text-gray-700 group-hover:text-black transition-colors" />
            </button>
          </div>

          <div className={`flex-auto flex flex-col items-center justify-center ${isTimesPost ? "" : "mt-0.5"}`}>
            <Link href={homeUrl} aria-label={`${pubName} home`} className="flex flex-col items-center">
              {hasPaddedTimesPostMasthead(siteSettings, logo) ? (
                <span className="relative block aspect-[1605/262] w-[min(62vw,200px)] overflow-hidden">
                  <img src={logo} alt={pubName} className="absolute" style={{ ...timesPostStickyCrop, maskImage: timesPostMastheadMask(siteSettings, logo), maskSize: "contain", maskRepeat: "no-repeat", maskPosition: "center" }} />
                </span>
              ) : logo ? (
                <img 
                  src={siteSettings?.organization?.logo || siteSettings?.organization?.logoSettings?.url} 
                  alt={pubName} 
                  className={isTimesPost ? "h-8 w-auto max-w-[min(62vw,200px)] object-contain" : "h-11 w-auto max-w-[210px] object-contain md:h-[64px] md:max-w-none lg:h-[72px]"}
                />
              ) : (
                <span className="text-xl md:text-2xl font-black font-classic tracking-tight leading-none text-black">
                  {pubName}
                </span>
              )}
              {tagLine && (!isTimesPost || isVisible) && <span className={isTimesPost ? "mt-1 text-[11px] font-classic italic leading-none text-gray-800" : "text-[12px] md:text-[14px] tracking-wider font-sans text-gray-500 leading-none -mt-3 md:-mt-4 hidden sm:block relative z-10"}>
                {tagLine}
              </span>}
            </Link>
          </div>

          <div className={`flex-1 flex items-center justify-end ${isTjt ? "md:invisible" : ""}`}>
            <MobileNotificationControl latestArticles={latestArticles} compact routePrefix={routePrefix} />
          </div>
        </div>
      </div>

      <StickyNavigationDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        siteSettings={siteSettings}
        publishedArticles={publishedArticles}
        routePrefix={routePrefix}
      />
    </>
  );
}











